import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Leaf, ExternalLink, Heart, Sparkles, Users, Crown } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import useSeo from '@/hooks/useSeo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const resolveMedia = (src) => {
  if (!src) return null;
  if (src.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${src}`;
  return src;
};

// Emerald/green instead of gold — distinct visual identity for non-profits
const ACCENT = '#10B981';
const ACCENT_LIGHT = '#34D399';

export default function FoundingNonProfitsPage() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useSeo({
    title: 'Founding Non-Profits · EntrepreNatives',
    description:
      'Meet The Ten — the first 10 non-profit organizations who partnered with EntrepreNatives to build a global Indigenous marketplace.',
    canonical: 'https://entreprenatives.com/founding-nonprofits',
    ogImage: 'https://entreprenatives.com/og-image.png',
  });

  useEffect(() => {
    (async () => {
      try {
        const [listRes, statusRes] = await Promise.all([
          fetch(`${API}/founding-nonprofits/list`),
          fetch(`${API}/founding-nonprofits/status`),
        ]);
        if (listRes.ok) setData(await listRes.json());
        if (statusRes.ok) setStatus(await statusRes.json());
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const founders = data?.vendors || [];
  const slotsTotal = status?.slots_total ?? 10;
  const slotsTaken = status?.slots_taken ?? 0;
  const slotsRemaining = status?.slots_remaining ?? 10;
  const price = status?.price_usd ?? 2500;
  const pct = Math.min(100, (slotsTaken / slotsTotal) * 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0E0E23] via-[#0f2a1e] to-[#0E0E23] text-white" data-testid="founding-nonprofits-page">
      <Navbar />

      <main className="pt-24 pb-20">
        <section className="container-main">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative overflow-hidden rounded-3xl border p-8 sm:p-14 mb-12"
            style={{
              borderColor: `${ACCENT}66`,
              background: 'linear-gradient(135deg, rgba(16,185,129,0.18) 0%, rgba(5,150,105,0.08) 45%, rgba(14,14,35,1) 100%)',
            }}
          >
            <div className="absolute -top-24 -right-24 w-[28rem] h-[28rem] rounded-full blur-[120px]" style={{ background: 'rgba(16,185,129,0.18)' }} />

            <div className="relative max-w-4xl">
              <div
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold mb-5"
                style={{ background: 'rgba(16,185,129,0.2)', border: `1px solid ${ACCENT}66`, color: ACCENT_LIGHT }}
              >
                <Leaf size={12} /> THE TEN · OUR ORIGINAL NON-PROFIT PARTNERS
              </div>

              <h1 className="font-syne text-4xl sm:text-6xl font-bold mb-5 leading-tight">
                These incredible organizations believed in <span style={{ color: ACCENT_LIGHT }}>EntrepreNatives</span> and helped us build a global <span style={{ color: ACCENT_LIGHT }}>Indigenous economy.</span>
              </h1>
              <p className="text-emerald-100/80 font-outfit text-base sm:text-lg mb-4 leading-relaxed">
                We call them <strong className="text-white">The Ten</strong> — the first 10 non-profit organizations who co-invested $2,500 each to help us take EntrepreNatives global.
                Their missions shape our platform. Their communities are the ones we serve. Their partnership made this possible.
              </p>
              <p className="text-emerald-200/60 font-outfit text-sm sm:text-base mb-6 italic">
                They're not just donors — they're co-architects, and their logos and missions live here permanently.
              </p>

              {/* Slot meter */}
              <div className="max-w-lg mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-emerald-100/60 font-semibold uppercase tracking-wide">
                    <span className="text-white font-bold" style={{ color: ACCENT_LIGHT }}>{slotsTaken}</span> of {slotsTotal} seats claimed
                  </span>
                  <span className="text-xs font-mono" style={{ color: ACCENT_LIGHT }} data-testid="nonprofit-slot-count">
                    {slotsRemaining} still open · ${price.toLocaleString()} each
                  </span>
                </div>
                <div className="h-3 rounded-full bg-white/5 border border-white/10 overflow-hidden">
                  <motion.div
                    className="h-full"
                    style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_LIGHT})` }}
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.8 }}
                  />
                </div>
              </div>

              {slotsRemaining > 0 && (
                <Link
                  to="/dashboard/vendor/founding-nonprofit"
                  className="inline-flex items-center gap-3 px-6 py-3 rounded-xl text-[#0E0E23] font-bold text-sm shadow-[0_10px_40px_-10px_rgba(16,185,129,0.6)]"
                  style={{ background: `linear-gradient(135deg, ${ACCENT_LIGHT} 0%, ${ACCENT} 100%)` }}
                  data-testid="nonprofit-public-cta"
                >
                  <Heart size={16} /> Become a Founding Non-Profit
                </Link>
              )}
            </div>
          </motion.div>

          {/* Roll-call */}
          {loading ? (
            <div className="text-center text-emerald-200/60 py-16">Loading founding non-profits…</div>
          ) : founders.length === 0 ? (
            <>
              <div className="flex items-center gap-2 mb-6">
                <Users className="w-5 h-5" style={{ color: ACCENT_LIGHT }} />
                <h2 className="font-syne text-2xl font-bold">The Ten — every seat is still open</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="nonprofit-grid">
                {Array.from({ length: slotsTotal }).map((_, i) => (
                  <EmptySlot key={`empty-all-${i}`} number={i + 1} />
                ))}
              </div>
            </>
          ) : (
            <>
              {founders[0] && <FeaturedNonProfitCard org={founders[0]} />}

              <div className="flex items-center gap-2 mb-6 mt-14">
                <Users className="w-5 h-5" style={{ color: ACCENT_LIGHT }} />
                <h2 className="font-syne text-2xl font-bold">
                  The Ten — {founders.length} claimed, {slotsRemaining} waiting
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="nonprofit-grid">
                {founders.slice(1).map((v, i) => (
                  <NonProfitCard key={v.vendor_id} org={v} index={i + 1} />
                ))}
                {Array.from({ length: slotsRemaining }).map((_, i) => (
                  <EmptySlot key={`empty-${i}`} number={slotsTaken + i + 1} />
                ))}
              </div>
            </>
          )}

          {/* Thanks */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 p-8 rounded-3xl border text-center"
            style={{ background: 'rgba(16,185,129,0.08)', borderColor: `${ACCENT}33` }}
          >
            <Sparkles className="w-8 h-8 mx-auto mb-3" style={{ color: ACCENT_LIGHT }} />
            <h3 className="font-syne text-xl font-bold mb-2">Thank you, partners.</h3>
            <p className="text-emerald-100/75 font-outfit max-w-xl mx-auto text-sm">
              Your mission is our mandate. Every Indigenous entrepreneur this platform lifts up — you helped make that possible.
            </p>
            <Link to="/founding-members" className="inline-flex items-center gap-2 mt-5 text-xs font-semibold text-emerald-300/70 hover:text-white transition">
              <Crown size={12} /> See also: The Fifty (Founding Vendors) →
            </Link>
          </motion.div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function FeaturedNonProfitCard({ org }) {
  const img = resolveMedia(org.profile_image) || resolveMedia(org.banner_image);
  return (
    <motion.div
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="relative mt-4"
      data-testid={`featured-nonprofit-${org.vendor_id}`}
    >
      <div className="absolute -inset-0.5 rounded-3xl opacity-90 blur-[2px]" style={{ background: `linear-gradient(135deg, ${ACCENT_LIGHT}, ${ACCENT}, ${ACCENT_LIGHT})` }} />
      <div className="relative rounded-3xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #062014 0%, #0a2e1f 55%, #0E0E23 100%)' }}>
        <div className="absolute top-4 left-4 z-20 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[#0E0E23] text-[11px] font-extrabold tracking-wider shadow-lg"
          style={{ background: `linear-gradient(135deg, ${ACCENT_LIGHT} 0%, ${ACCENT} 100%)` }}>
          <Leaf size={12} /> FOUNDING NON-PROFIT #{org.founding_nonprofit_number}
        </div>
        <div className="absolute top-4 right-4 z-20 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-[10px] font-mono tracking-wide" style={{ color: ACCENT_LIGHT, border: `1px solid ${ACCENT}66` }}>
          THE ORIGINAL
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1.1fr,1.2fr] min-h-[360px]">
          <div className="relative aspect-square md:aspect-auto bg-emerald-950/40 overflow-hidden">
            {img ? (
              <img src={img} alt={org.business_name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${ACCENT}20, ${ACCENT_LIGHT}10)` }}>
                <Leaf className="w-24 h-24" style={{ color: `${ACCENT}66` }} />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-tr from-[#0E0E23]/80 via-transparent to-transparent" />
          </div>

          <div className="p-6 md:p-10 flex flex-col justify-center">
            <h2 className="font-syne text-3xl md:text-4xl font-bold text-white leading-tight mb-2" data-testid="featured-nonprofit-name">
              {org.business_name}
            </h2>
            {org.owner_name && (
              <p className="font-outfit font-semibold text-sm mb-4" style={{ color: `${ACCENT_LIGHT}cc` }}>Led by {org.owner_name}</p>
            )}
            <div className="flex flex-wrap gap-2 mb-5">
              {org.nonprofit_url && (
                <a href={org.nonprofit_url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full font-semibold"
                  style={{ background: `${ACCENT}20`, color: ACCENT_LIGHT, border: `1px solid ${ACCENT}66` }}>
                  <ExternalLink size={10} /> Official site
                </a>
              )}
              {(org.service_city || org.service_state) && (
                <span className="text-xs px-3 py-1 rounded-full bg-white/5 text-emerald-200/70 border border-white/10">
                  {[org.service_city, org.service_state].filter(Boolean).join(', ')}
                </span>
              )}
            </div>
            {org.founding_nonprofit_story ? (
              <p className="text-emerald-100/90 font-outfit leading-relaxed text-sm md:text-base mb-6 line-clamp-6">
                {org.founding_nonprofit_story}
              </p>
            ) : (
              <p className="italic text-emerald-200/60 font-outfit text-sm mb-6">
                The very first organization to believe in this platform.
              </p>
            )}
            <Link
              to={`/vendor/${org.vendor_id}`}
              className="inline-flex items-center gap-2 self-start px-6 py-3 rounded-xl text-[#0E0E23] font-bold text-sm shadow-[0_10px_30px_-10px_rgba(16,185,129,0.6)]"
              style={{ background: `linear-gradient(135deg, ${ACCENT_LIGHT} 0%, ${ACCENT} 100%)` }}
              data-testid="featured-nonprofit-cta"
            >
              Visit organization <ExternalLink size={14} />
            </Link>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function NonProfitCard({ org, index }) {
  const img = resolveMedia(org.profile_image) || resolveMedia(org.banner_image);
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: (index % 6) * 0.05 }}
      whileHover={{ y: -4 }}
      className="group relative"
      data-testid={`nonprofit-card-${org.vendor_id}`}
    >
      <div className="absolute -inset-[1px] rounded-2xl opacity-60 group-hover:opacity-100 transition blur-[1px]"
        style={{ background: `linear-gradient(135deg, ${ACCENT}99, ${ACCENT_LIGHT}33, ${ACCENT}99)` }} />
      <div className="relative rounded-2xl overflow-hidden transition group-hover:border-emerald-300/70"
        style={{ background: 'linear-gradient(180deg, #062014 0%, #0a1628 100%)', border: `1px solid ${ACCENT}4d` }}>
        <div className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[#0E0E23] text-[10px] font-extrabold tracking-wider shadow"
          style={{ background: `linear-gradient(135deg, ${ACCENT_LIGHT} 0%, ${ACCENT} 100%)` }}>
          <Leaf size={10} /> #{org.founding_nonprofit_number}
        </div>

        <div className="aspect-[4/3] w-full bg-emerald-950/40 overflow-hidden relative">
          {img ? (
            <img src={img} alt={org.business_name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${ACCENT}20, ${ACCENT_LIGHT}10)` }}>
              <Leaf className="w-14 h-14" style={{ color: `${ACCENT}80` }} />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0E0E23] via-transparent to-transparent opacity-80" />
        </div>

        <div className="p-5">
          <div className="flex items-start justify-between gap-2 mb-1.5">
            <h3 className="font-syne text-base font-bold text-white leading-snug">{org.business_name}</h3>
            <Link
              to={`/vendor/${org.vendor_id}`}
              className="shrink-0 opacity-60 group-hover:opacity-100 hover:text-white transition"
              style={{ color: ACCENT_LIGHT }}
              aria-label="View profile"
            >
              <ExternalLink size={15} />
            </Link>
          </div>
          {org.owner_name && (
            <p className="text-[11px] font-outfit font-semibold mb-3" style={{ color: `${ACCENT_LIGHT}cc` }}>
              Led by {org.owner_name}
            </p>
          )}
          {(org.service_city || org.service_state) && (
            <span className="inline-block text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-emerald-200/70 border border-white/10 mb-3">
              {[org.service_city, org.service_state].filter(Boolean).join(', ')}
            </span>
          )}
          {org.founding_nonprofit_story && (
            <p className="text-xs text-emerald-100/80 font-outfit leading-relaxed line-clamp-4 mb-4">
              {org.founding_nonprofit_story}
            </p>
          )}
          <Link
            to={`/vendor/${org.vendor_id}`}
            className="flex items-center justify-center gap-1.5 py-2 rounded-lg text-[10px] font-bold tracking-wide uppercase transition hover:bg-emerald-500/10"
            style={{ color: ACCENT_LIGHT, border: `1px solid ${ACCENT}4d` }}
          >
            <Leaf size={10} /> Visit organization
          </Link>
        </div>
      </div>
    </motion.div>
  );
}

function EmptySlot({ number }) {
  return (
    <Link
      to="/dashboard/vendor/founding-nonprofit"
      className="group rounded-2xl p-6 flex flex-col items-center justify-center min-h-[340px] text-center transition hover:bg-emerald-500/[0.06]"
      style={{ border: `1px dashed ${ACCENT}40`, background: 'rgba(255,255,255,0.02)' }}
      data-testid={`empty-nonprofit-slot-${number}`}
    >
      <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4 transition group-hover:scale-110"
        style={{ border: `2px solid ${ACCENT}66` }}>
        <Leaf className="w-6 h-6 transition" style={{ color: `${ACCENT_LIGHT}99` }} />
      </div>
      <p className="text-[10px] font-mono tracking-widest uppercase mb-2" style={{ color: 'rgba(255,255,255,0.6)' }}>Seat #{number} · Open</p>
      <p className="font-syne text-base font-bold text-white mb-1">This seat is waiting</p>
      <p className="text-xs font-bold tracking-wide uppercase mb-4" style={{ color: ACCENT_LIGHT }}>Become a Founding Non-Profit</p>
      <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[11px] font-bold text-[#0E0E23] shadow group-hover:scale-105 transition"
        style={{ background: `linear-gradient(135deg, ${ACCENT_LIGHT} 0%, ${ACCENT} 100%)` }}>
        <Heart size={11} /> Claim seat #{number}
      </span>
    </Link>
  );
}
